import {
  Box,
  Card,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  Tooltip,
  Button,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ReferralUsersResponse } from "../types";
import { fetchUsersByReferrals, getReferralCount } from "../utils/referrals-api";

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

export function ReferralsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, error } = useQuery<
    ReferralUsersResponse,
    Error
  >({
    queryKey: ["referrals", page, limit, search],
    queryFn: () => fetchUsersByReferrals({ page, limit, search }),
  });

  const items = data?.items ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;

  const handleResetFilters = () => {
    setSearch("");
    setPage(1);
  };

  return (
    <Card sx={{ p: isMobile ? 2 : 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Referrals
        </Typography>
        {data?.meta?.totalItems !== undefined ? (
          <Typography variant="body2" color="text.secondary">
            {data.meta.totalItems} users
          </Typography>
        ) : null}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Username, telegram ID, email"
            sx={{ minWidth: isMobile ? "100%" : 240 }}
          />
          <Button variant="text" onClick={handleResetFilters}>
            Clear Filters
          </Button>
          <Typography variant="caption" color="text.secondary">
            Sorted by referral count, most first.
          </Typography>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        {isError ? (
          <Box p={3} textAlign="center">
            <Typography color="error" variant="body1">
              {(error as Error).message}
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box p={2}>
            {[...Array(4)].map((_, index) => (
              <Box key={index} display="flex" gap={2} py={2} px={2}>
                <Skeleton variant="text" width="15%" height={30} />
                <Skeleton variant="text" width="25%" height={30} />
                <Skeleton variant="text" width="25%" height={30} />
                <Skeleton variant="text" width="20%" height={30} />
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Telegram ID</TableCell>
                  <TableCell>Referrals</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No referrals found.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {user.username || `User ${user.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>{user.telegramId || "—"}</TableCell>
                      <TableCell>{getReferralCount(user) ?? "—"}</TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Referrals">
                          <IconButton
                            onClick={() => navigate(`/referrals/${user.id}`)}
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          size={isMobile ? "small" : "large"}
        />
      </Box>
    </Card>
  );
}
